// TableauPatPicker — saved-PAT selector that the TableauOutputsPanel
// embeds when the user opts into Publish or Pulse (both need creds).
//
// Behaviour:
//   - Lists saved profiles for the current SE via GET /profiles.
//   - Marks the default profile with a ★ chip.
//   - "+ Add Tableau PAT" opens TableauPatTestModal; on save the
//     query cache is invalidated and the new profile is auto-selected.
//   - "Use one-off" path stays in the modal — caller wires the
//     one-off creds into TableauOutputsConfig.patOneOff.
//
// The component is intentionally read-only when no profile is
// selected; the parent disables Publish/Pulse if neither
// patProfileId nor patOneOff is set.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { TableauPatTestModal } from "./TableauPatTestModal";
import type { TableauPatOneOff, TableauProfile } from "@/lib/types";

interface TableauPatPickerProps {
  selectedProfileId: string | null;
  oneOff: TableauPatOneOff | null;
  onSelectProfile: (profileId: string | null) => void;
  onSetOneOff: (oneOff: TableauPatOneOff | null) => void;
}

export function TableauPatPicker({
  selectedProfileId,
  oneOff,
  onSelectProfile,
  onSetOneOff,
}: TableauPatPickerProps): JSX.Element {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const { data: profiles = [], isLoading } = useQuery({
    queryKey: ["tableau-profiles"],
    queryFn: api.listTableauProfiles,
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.deleteTableauProfile(id),
    onSuccess: (_, id) => {
      if (selectedProfileId === id) onSelectProfile(null);
      qc.invalidateQueries({ queryKey: ["tableau-profiles"] });
    },
  });

  // Auto-select default profile on first load if nothing chosen yet.
  const defaultProfile = profiles.find((p) => p.isDefault);
  if (
    !isLoading
    && !selectedProfileId
    && !oneOff
    && defaultProfile
    && profiles.length > 0
  ) {
    // Defer state update to the next tick so React doesn't error
    // ("setState during render"). Using queueMicrotask keeps the
    // selection visible on the very first paint after profiles load.
    queueMicrotask(() => onSelectProfile(defaultProfile.id));
  }

  return (
    <div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: ".06em",
          textTransform: "uppercase",
          color: "var(--ink-3)",
          marginBottom: 6,
        }}
      >
        Tableau Cloud PAT
      </div>
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <select
          value={oneOff ? "__oneoff__" : selectedProfileId ?? ""}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "__oneoff__") return; // read-only marker
            onSetOneOff(null);
            onSelectProfile(v || null);
          }}
          style={{
            padding: "8px 10px",
            fontSize: 12,
            border: "1px solid var(--bd)",
            borderRadius: 6,
            background: "white",
            color: "var(--ink)",
            minWidth: 240,
          }}
        >
          <option value="">— Select a saved PAT —</option>
          {profiles.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label} {p.isDefault ? "★" : ""} ({p.siteName})
            </option>
          ))}
          {oneOff && (
            <option value="__oneoff__">
              One-off · {oneOff.siteName} ({oneOff.patName})
            </option>
          )}
        </select>
        <button
          type="button"
          className="btn ghost sm"
          onClick={() => setModalOpen(true)}
        >
          + Add Tableau PAT
        </button>
        {selectedProfileId && (
          <button
            type="button"
            className="btn ghost sm"
            onClick={() => {
              if (
                window.confirm(
                  "Delete this saved Tableau PAT profile? Future runs will need to re-enter the secret.",
                )
              ) {
                deleteMut.mutate(selectedProfileId);
              }
            }}
            style={{ color: "var(--ink-3)" }}
          >
            Delete
          </button>
        )}
      </div>

      {/* Selected-profile metadata */}
      {selectedProfileId && (
        <ProfileSummary
          profile={profiles.find((p) => p.id === selectedProfileId) ?? null}
        />
      )}
      {oneOff && (
        <div
          style={{
            marginTop: 8,
            fontSize: 11,
            color: "var(--ink-2)",
            background: "rgba(245,166,35,.08)",
            border: "1px solid rgba(245,166,35,.3)",
            padding: "6px 10px",
            borderRadius: 6,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>
            Using one-off PAT for <strong>{oneOff.siteName}</strong>. The
            secret is sent with this run only and isn&rsquo;t stored.
          </span>
          <button
            type="button"
            className="btn ghost sm"
            onClick={() => onSetOneOff(null)}
          >
            Clear
          </button>
        </div>
      )}

      {modalOpen && (
        <TableauPatTestModal
          onClose={() => setModalOpen(false)}
          onUseOneOff={(creds) => {
            onSelectProfile(null);
            onSetOneOff(creds);
          }}
          onSavedProfile={(profile) => {
            onSetOneOff(null);
            onSelectProfile(profile.id);
          }}
        />
      )}
    </div>
  );
}

function ProfileSummary({
  profile,
}: {
  profile: TableauProfile | null;
}): JSX.Element | null {
  if (!profile) return null;
  return (
    <div
      style={{
        marginTop: 8,
        padding: "8px 10px",
        background: "var(--cb-98)",
        borderRadius: 6,
        fontSize: 11,
        color: "var(--ink-2)",
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        rowGap: 4,
        columnGap: 12,
      }}
    >
      <span style={{ color: "var(--ink-3)" }}>Server</span>
      <span style={{ color: "var(--ink)" }}>{profile.serverUrl}</span>
      <span style={{ color: "var(--ink-3)" }}>Site</span>
      <span style={{ color: "var(--ink)" }}>{profile.siteName}</span>
      <span style={{ color: "var(--ink-3)" }}>PAT name</span>
      <span style={{ color: "var(--ink)" }}>{profile.patName}</span>
      {profile.lastValidatedAt && (
        <>
          <span style={{ color: "var(--ink-3)" }}>Last validated</span>
          <span style={{ color: "var(--ink)" }}>
            {new Date(profile.lastValidatedAt).toLocaleString()}
          </span>
        </>
      )}
    </div>
  );
}
